import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { AuditableFiltersModel } from '../../../Common/Filter/models/auditable-filters.model';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { ListEntitiesVisibilityEnum } from '../../../Common/MonitorShared/enum/list-entities-visibility.enum';
import { FilterTypeRadioModel } from '../../../Common/Filter/models/filter-type-radio.model';
import { FilterTypeAutoTaggingTopicsCheckboxModel } from '../../../Common/Filter/models/filter-type-auto-tagging-topics-checkbox.model';
import {
    AutoTaggingTopicsCheckboxFilterComponent
} from '../../../Common/Filter/components/AutoTaggingTopicsCheckboxFilter/auto-tagging-topics-checkbox-filter.component';
import {
    UserAppPreferenceMonitoringCommonFilters,
    UserAppPreferenceTopicsFilters,
    UserAppPreferenceCoverageAlertsFilters,
    UserAppPreferenceBriefingsFilters,
    UserAppPreferenceSubscriptionsFilters,
} from '../../../Core/Users/models/user-app-preferences-filters.model';

export class MonitoringCommonDisplayFilterModel extends AuditableFiltersModel {
    private static readonly VISIBILITY_ID = 'visibility';
    protected readonly visibilityFilter = new FilterTypeRadioModel<ListEntitiesVisibilityEnum>(
        MonitoringCommonDisplayFilterModel.VISIBILITY_ID,
        'Monitoring.filter.visibility',
        [
            {
                label: 'Monitoring.filter.visibility.all',
                value: ListEntitiesVisibilityEnum.all
            },
            {
                label: 'Monitoring.filter.visibility.visible',
                value: ListEntitiesVisibilityEnum.visible
            },
            {
                label: 'Monitoring.filter.visibility.hidden',
                value: ListEntitiesVisibilityEnum.hidden
            }
        ]
    );

    protected filterValues: UserAppPreferenceMonitoringCommonFilters;

    constructor(filters: UserAppPreferenceMonitoringCommonFilters) {
        super(filters);
        this.visibilityFilter.setValue(filters.visibility);
        this.filterValues = {
            ...this.auditableValues,
            visibility: filters.visibility
        };
    }

    getAllFilters(): FilterTypeModel<any>[] {
        return [this.visibilityFilter];
    }

    public setAllFilters(filtersToSet: FilterTypeModel<any>[], filterValues: UserAppPreferenceMonitoringCommonFilters) {
        super.setAuditableFilters(filtersToSet, filterValues);
        this.visibilityFilter.setValue(filterValues.visibility);
    }

    public setNewValues(newFilters: FilterTypeModel<any>[]) {
        super.setNewAuditableFilterValues(newFilters);
        this.filterValues = {
            ...this.auditableValues,
            visibility: newFilters.find(filter => filter.id === MonitoringCommonDisplayFilterModel.VISIBILITY_ID)?.getValue()
        };
    }

    public getFilterValues(): UserAppPreferenceMonitoringCommonFilters {
        return {
            ...super.getAuditableFilterValues(),
            visibility: this.filterValues.visibility
        };
    }
}

export class MonitoringTopicsDisplayFilterModel extends MonitoringCommonDisplayFilterModel {
    private static readonly AUTO_TAG_ID = 'autotags';
    private static readonly AUTO_SAVING_ID = 'autoSaving';
    private readonly autoTaggingFilter = new FilterTypeAutoTaggingTopicsCheckboxModel(
        MonitoringTopicsDisplayFilterModel.AUTO_TAG_ID,
        'Monitor.leftSidebar.topics.filter.autoTag',
        AutoTaggingTopicsCheckboxFilterComponent,
        [],
        undefined,
        true
    );
    private readonly autoSavingFilter = new FilterTypeRadioModel<ListEntitiesVisibilityEnum>(
        MonitoringTopicsDisplayFilterModel.AUTO_SAVING_ID,
        'Monitoring.filter.autoSaving',
        []
    );

    protected declare filterValues: UserAppPreferenceTopicsFilters;

    constructor(filters: UserAppPreferenceTopicsFilters) {
        super(filters);
        this.autoTaggingFilter.setValue(filters.autoTag);
        this.autoSavingFilter.setValue(filters.autoSaving);
        this.filterValues = {
            ...this.auditableValues,
            visibility: this.filterValues.visibility,
            autoTag: filters.autoTag,
            autoSaving: filters.autoSaving
        };
    }

    getAllFilters(): FilterTypeModel<any>[] {
        return [this.visibilityFilter, this.autoTaggingFilter, this.autoSavingFilter];
    }

    public setAllFilters(filtersToSet: FilterTypeModel<any>[], filterValues: UserAppPreferenceTopicsFilters) {
        filtersToSet.find(filter => filter.id === 'name').label = 'UserPreferences.topicName';
        super.setAllFilters(filtersToSet, filterValues);
        this.autoTaggingFilter.setValue(filterValues.autoTag);
        this.autoSavingFilter.setValue(filterValues.autoSaving);
    }

    public setNewValues(newFilters: FilterTypeModel<any>[]) {
        super.setNewValues(newFilters);
        this.filterValues = {
            ...this.auditableValues,
            visibility: this.filterValues.visibility,
            autoTag: newFilters.find(filter => filter.id === MonitoringTopicsDisplayFilterModel.AUTO_TAG_ID)?.getValue(),
            autoSaving: newFilters.find(filter => filter.id === MonitoringTopicsDisplayFilterModel.AUTO_SAVING_ID)?.getValue(),
        };
    }

    public getFilterValues(): UserAppPreferenceTopicsFilters {
        return {
            ...super.getAuditableFilterValues(),
            visibility: this.filterValues.visibility,
            autoTag: this.filterValues.autoTag,
            autoSaving: this.filterValues.autoSaving
        };
    }
}

export class MonitoringSavedCoverageDisplayFilterModel extends MonitoringCommonDisplayFilterModel {

    protected declare filterValues: UserAppPreferenceMonitoringCommonFilters;

    public setAllFilters(filtersToSet: FilterTypeModel<any>[], filterValues: UserAppPreferenceMonitoringCommonFilters) {
        filtersToSet.find(filter => filter.id === 'name').label = 'UserPreferences.folderName';
        super.setAllFilters(filtersToSet, filterValues);
    }

    public getFilterValues(): UserAppPreferenceMonitoringCommonFilters {
        return {
            ...super.getAuditableFilterValues(),
            visibility: this.filterValues.visibility,
        };
    }
}

export class MonitoringReportsDisplayFilterModel extends MonitoringCommonDisplayFilterModel {

    protected declare filterValues: UserAppPreferenceMonitoringCommonFilters;

    public setAllFilters(filtersToSet: FilterTypeModel<any>[], filterValues: UserAppPreferenceMonitoringCommonFilters) {
        filtersToSet.find(filter => filter.id === 'name').label = 'UserPreferences.reportName';
        super.setAllFilters(filtersToSet, filterValues);
    }

    public getFilterValues(): UserAppPreferenceMonitoringCommonFilters {
        return {
            ...super.getAuditableFilterValues(),
            visibility: this.filterValues.visibility,
        };
    }
}

export class MonitoringBriefingsDisplayFilterModel extends MonitoringCommonDisplayFilterModel {
    private static readonly STATUS_ID = 'status';
    private static readonly DELIVERY_METHOD_ID = 'deliveryMethod';
    private readonly statusFilter = new FilterTypeCheckboxModel<string>(
        MonitoringBriefingsDisplayFilterModel.STATUS_ID,
        'Status',
        [],
        true
    );

    private readonly deliveryMethodFilter = new FilterTypeCheckboxModel<string>(
        MonitoringBriefingsDisplayFilterModel.DELIVERY_METHOD_ID,
        'DeliveryMethod',
        [],
        true
    );

    protected declare filterValues: UserAppPreferenceBriefingsFilters;

    constructor(filters: UserAppPreferenceBriefingsFilters) {
        super(filters);
        this.statusFilter.setValue(filters.status);
        this.deliveryMethodFilter.setValue(filters.deliveryMethod);
        this.filterValues = {
            ...this.auditableValues,
            visibility: this.filterValues.visibility,
            status: filters.status,
            deliveryMethod: filters.deliveryMethod
        };
    }

    getAllFilters(): FilterTypeModel<any>[] {
        return [this.visibilityFilter, this.statusFilter];
    }

    public setAllFilters(filtersToSet: FilterTypeModel<any>[], filterValues: UserAppPreferenceBriefingsFilters) {
        filtersToSet.find(filter => filter.id === 'name').label = 'UserPreferences.briefingName';
        super.setAllFilters(filtersToSet, filterValues);
        this.statusFilter.setValue(filterValues.status);
    }

    public setNewValues(newFilters: FilterTypeModel<any>[]) {
        super.setNewValues(newFilters);
        this.filterValues = {
            ...this.auditableValues,
            visibility: this.filterValues.visibility,
            status: newFilters.find(filter => filter.id === MonitoringBriefingsDisplayFilterModel.STATUS_ID)?.getValue(),
            deliveryMethod: newFilters.find(filter => filter.id === MonitoringBriefingsDisplayFilterModel.DELIVERY_METHOD_ID)?.getValue()
        };
    }

    public getFilterValues(): UserAppPreferenceBriefingsFilters {
        return {
            ...super.getAuditableFilterValues(),
            visibility: this.filterValues.visibility,
            status: this.filterValues.status,
            deliveryMethod: this.filterValues.deliveryMethod
        };
    }
}

export class MonitoringCoverageAlertsDisplayFilterModel extends MonitoringCommonDisplayFilterModel {
    private static readonly STATUS_ID = 'status';
    private static readonly ALERT_TYPE_ID = 'alertType';
    private readonly statusFilter = new FilterTypeCheckboxModel<string>(
        MonitoringCoverageAlertsDisplayFilterModel.STATUS_ID,
        'Status',
        [],
        true
    );
    private readonly alertTypeFilter = new FilterTypeCheckboxModel<string>(
        MonitoringCoverageAlertsDisplayFilterModel.ALERT_TYPE_ID,
        'AlertType',
        [],
        true
    );

    protected declare filterValues: UserAppPreferenceCoverageAlertsFilters;

    constructor(filters: UserAppPreferenceCoverageAlertsFilters) {
        super(filters);
        this.statusFilter.setValue(filters.status);
        this.alertTypeFilter.setValue(filters.alertType);
        this.filterValues = {
            ...this.auditableValues,
            visibility: this.filterValues.visibility,
            status: filters.status,
            alertType: filters.alertType
        };
    }

    getAllFilters(): FilterTypeModel<any>[] {
        return [this.visibilityFilter, this.statusFilter];
    }

    public setAllFilters(filtersToSet: FilterTypeModel<any>[], filterValues: UserAppPreferenceCoverageAlertsFilters) {
        filtersToSet.find(filter => filter.id === 'name').label = 'UserPreferences.coverageAlertName';
        super.setAllFilters(filtersToSet, filterValues);
        this.statusFilter.setValue(filterValues.status);
    }

    public setNewValues(newFilters: FilterTypeModel<any>[]) {
        super.setNewValues(newFilters);
        this.filterValues = {
            ...this.auditableValues,
            visibility: this.filterValues.visibility,
            status: newFilters.find(filter => filter.id === MonitoringCoverageAlertsDisplayFilterModel.STATUS_ID)?.getValue(),
            alertType: newFilters.find(filter => filter.id === MonitoringCoverageAlertsDisplayFilterModel.ALERT_TYPE_ID)?.getValue()
        };
    }

    public getFilterValues(): UserAppPreferenceCoverageAlertsFilters {
        return {
            ...super.getAuditableFilterValues(),
            visibility: this.filterValues.visibility,
            status: this.filterValues.status,
            alertType: this.filterValues.alertType
        };
    }
}

export class MonitoringSubscriptionsDisplayFilterModel extends MonitoringCommonDisplayFilterModel {
    private static readonly STATUS_ID = 'status';
    private static readonly TYPE_ID = 'type';
    private readonly statusFilter = new FilterTypeCheckboxModel<string>(
        MonitoringSubscriptionsDisplayFilterModel.STATUS_ID,
        'Status',
        [],
        true
    );
    private readonly typeFilter = new FilterTypeCheckboxModel<string>(
        MonitoringSubscriptionsDisplayFilterModel.TYPE_ID,
        'Type',
        [],
        true
    );

    protected declare filterValues: UserAppPreferenceSubscriptionsFilters;

    constructor(filters: UserAppPreferenceSubscriptionsFilters) {
        super(filters);
        this.statusFilter.setValue(filters.status);
        this.typeFilter.setValue(filters.type);
        this.filterValues = {
            ...this.auditableValues,
            visibility: this.filterValues.visibility,
            status: filters.status,
            type: filters.type
        };
    }

    getAllFilters(): FilterTypeModel<any>[] {
        return [this.visibilityFilter, this.statusFilter, this.typeFilter];
    }

    public setAllFilters(filtersToSet: FilterTypeModel<any>[], filterValues: UserAppPreferenceSubscriptionsFilters) {
        filtersToSet.find(filter => filter.id === 'name').label = 'UserPreferences.subscriptionName';
        super.setAllFilters(filtersToSet, filterValues);
        this.statusFilter.setValue(filterValues.status);
        this.typeFilter.setValue(filterValues.type);
    }

    public setNewValues(newFilters: FilterTypeModel<any>[]) {
        super.setNewValues(newFilters);
        this.filterValues = {
            ...this.auditableValues,
            visibility: this.filterValues.visibility,
            status: newFilters.find(filter => filter.id === MonitoringSubscriptionsDisplayFilterModel.STATUS_ID)?.getValue(),
            type: newFilters.find(filter => filter.id === MonitoringSubscriptionsDisplayFilterModel.TYPE_ID)?.getValue()
        };
    }

    public getFilterValues(): UserAppPreferenceSubscriptionsFilters {
        return {
            ...super.getAuditableFilterValues(),
            visibility: this.filterValues.visibility,
            status: this.filterValues.status,
            type: this.filterValues.type
        };
    }
}
